import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { isEmpty, isNil } from "lodash";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  Collapse,
  LinearProgress,
  List,
  ListItem,
} from "@material-ui/core";

import { FleetContext } from "../../context";
import { FilledSelect } from "../../components/FormElements";

const styles = {
  bullets: {
    padding: 0,
    display: "list-item",
    listStyleType: "disc",
    listStylePosition: "inside",
    pl: 2,
  },
  warning: {
    paddingTop: 20,
  },
  partType: {
    paddingTop: 20,
    paddingBottom: 30,
  },
  buttons: {
    paddingTop: 20,
    paddingRight: 30,
    paddingBottom: 30,
  },
};

const RemoveComponent = () => {
  const partTypes = ["", "bm", "md", "sc", "sim"];
  const { t } = useTranslation();
  const { vehicleId } = useParams();
  const {
    removeSerializedComponent, removeSim, activeVehicle, activeSIMConfig,
    fetchActiveSIMConfig,
  } = useContext(FleetContext);
  const [isOpen, setIsOpen] = useState(true);
  const [partType, setPartType] = useState(partTypes[0]);
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [failure, setFailure] = useState(null);
  const [partSN, setPartSN] = useState(null);
  const history = useHistory();
  const cluster = window.REACT_APP_CLUSTER_NAME;
  const vehicleComponents = activeVehicle?.components;

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    setFailure(null);
    setIsLoading(true);

    try {
      if (partType === "sim") {
        await removeSim(activeVehicle.serial_number, partSN, cluster);
      } else {
        await removeSerializedComponent(
          activeVehicle.serial_number,
          partType,
          partSN,
          cluster,
        );
      }
      handleClose();
      await fetchActiveSIMConfig(vehicleId);
    } catch (err) {
      console.error(err);
      setFailure(parseError(err));
    }

    setIsValid(false);
    setIsLoading(false);
  };

  const parseError = (err) => {
    if (err.response && err.response.data) {
      if (err.response.data.error) {
        return err.response.data.error;
      }
      if (err.response.data instanceof Array) {
        let message = "";
        err.response.data.forEach((x) => {
          if (x instanceof Object) {
            // remove big whitespaces
            message += `${x.error.replace(/\s+/g, " ")} `;
          } else {
            message += `${x} `;
          }
        });
        return message;
      }
    }
    return err.message;
  };

  const getComponent = (type) => {
    if (vehicleComponents && vehicleComponents.length > 0) {
      const filteredComponent = vehicleComponents.filter(
        (component) => component.component_type === type,
      );

      const componentSerialNumber =
        filteredComponent.length > 0 ? filteredComponent[0].serial_number : null;

      return componentSerialNumber;
    }
    return null;
  };

  const getSim = () => {
    if (!isEmpty(activeSIMConfig)) {
      const simSerialNumber = activeSIMConfig?.links?.cellular?.length
        ? activeSIMConfig.links.cellular[0].sim : null;

      return simSerialNumber;
    }
    return null;
  };

  useEffect(() => {
    // validation
    setIsValid(false);
    switch (partType) {
      case "sc":
        if (activeVehicle?.serial_number) setIsValid(true);
        if (!isNil(vehicleComponents) && !isEmpty(vehicleComponents)) setPartSN(getComponent("systemcontroller"));
        break;
      case "md":
        if (activeVehicle) setIsValid(true);
        if (!isNil(vehicleComponents) && !isEmpty(vehicleComponents)) setPartSN(getComponent("motorcontroller"));
        break;
      case "bm":
        if (activeVehicle) setIsValid(true);
        if (!isNil(vehicleComponents) && !isEmpty(vehicleComponents)) setPartSN(getComponent("battery"));
        break;
      case "sim":
        if (activeVehicle?.serial_number) setIsValid(true);
        if (!isNil(activeSIMConfig) && !isEmpty(activeSIMConfig)){
          setPartSN(getSim());
        }
        break;
      default:
        setIsValid(false);
    }
  }, [partType, activeVehicle, activeSIMConfig]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("Vehicles.RemoveComponent.RemoveComponent")}</DialogTitle>
      <DialogContent>
        <Typography>{`
          ${activeVehicle?.shortcodes &&
          activeVehicle?.shortcodes.length &&
          activeVehicle?.shortcodes[0].shortcode
          } ${t("Vehicles.RemoveComponent.expected")}:
        `}</Typography>
        <List component="ul">
          <ListItem style={styles.bullets}>{t("Vehicles.RemoveComponent.text1")}</ListItem>
          <ListItem style={styles.bullets}>{t("Vehicles.RemoveComponent.text2")}</ListItem>
        </List>
        <Typography style={styles.warning}>{t("Vehicles.RemoveComponent.Warning")}</Typography>
        <FilledSelect
          label={t("Vehicles.RemoveComponent.PartType")}
          value={partType}
          onChange={(e) => setPartType(e.target.value)}
          FormControlProps={{ fullWidth: true, margin: "normal" }}
        >
          {partTypes.map(
            (pt) =>
              pt && (
                <MenuItem value={pt} key={pt}>
                  {t(`Vehicles.PartTypes.${pt}`)}
                </MenuItem>
              ),
          )}
        </FilledSelect>
        <Typography style={styles.partType}>
          {partType && `${t(`Vehicles.RemoveComponent.${partType}`)}:`} {partSN}
        </Typography>
        <Collapse in={isLoading} timeout={1000}>
          <LinearProgress color="secondary" />
        </Collapse>
        <Collapse in={!!failure} timeout={1000}>
          <Typography color="error">{failure}</Typography>
        </Collapse>
      </DialogContent>
      <DialogActions style={styles.buttons}>
        <Button onClick={handleClose}>{t("Vehicles.RemoveComponent.Cancel")}</Button>
        <Button onClick={handleSubmit} disabled={!isValid}>
          {t("Vehicles.RemoveComponent.Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveComponent;